import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, brl, formatCNPJ, formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MagnifyingGlass, Plus, Trash, CheckCircle, XCircle, ArrowRight, ArrowLeft,
  UserPlus, CloudArrowDown, Buildings, Package,
} from "@phosphor-icons/react";

const STEPS = ["Cliente", "Itens", "Revisão"];

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEPS.map((s, i) => {
        const state = i < step ? "done" : i === step ? "active" : "next";
        return (
          <React.Fragment key={s}>
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold
                ${state === "active" ? "bg-accent text-white" : state === "done" ? "bg-zinc-950 text-white" : "bg-zinc-200 text-zinc-500"}`}>
                {state === "done" ? <CheckCircle size={18} weight="fill" /> : i + 1}
              </div>
              <span className={`text-sm font-medium ${state === "next" ? "text-zinc-400" : "text-zinc-900"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border min-w-[20px]" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function OrderWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [priceTables, setPriceTables] = useState([]);
  const [maxDesc, setMaxDesc] = useState(10);
  const [pedidoMinimo, setPedidoMinimo] = useState(1500);

  const [cliQuery, setCliQuery] = useState("");
  const [client, setClient] = useState(null);
  const [priceTableId, setPriceTableId] = useState("");
  const [prazo, setPrazo] = useState("");

  const [prodQuery, setProdQuery] = useState("");
  const [items, setItems] = useState([]);
  const [obs, setObs] = useState("");
  const [faturamento, setFaturamento] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // new client dialog
  const [cnpjOpen, setCnpjOpen] = useState(false);
  const [cnpj, setCnpj] = useState("");
  const [newCli, setNewCli] = useState(null);
  const [looking, setLooking] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/price-tables").then((r) => setPriceTables(r.data.filter((p) => p.active)));
    api.get("/settings").then((r) => { setMaxDesc(r.data.max_desconto_pct); setPedidoMinimo(r.data.pedido_minimo ?? 1500); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/clients", { params: { q: cliQuery || undefined, limit: 20 } })
        .then((r) => setClients(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [cliQuery]);

  const selectedTable = priceTables.find((p) => p.id === priceTableId);
  const prazoOptions = selectedTable?.prazos_permitidos || [];
  const prazoValido = !!selectedTable && prazo !== "" && prazoOptions.includes(prazo);
  const effectiveMaxDesc = selectedTable && selectedTable.max_desconto_pct != null ? selectedTable.max_desconto_pct : maxDesc;

  const filteredClients = clients.slice(0, 12);

  const filteredProducts = prodQuery
    ? products.filter((p) => p.codigo?.toLowerCase().includes(prodQuery.toLowerCase()) || p.descricao?.toLowerCase().includes(prodQuery.toLowerCase())).slice(0, 8)
    : [];

  const priceOf = (p) => (p.prices && priceTableId in p.prices) ? p.prices[priceTableId] : 0;

  const addProduct = (p) => {
    if (items.find((it) => it.produto_id === p.id)) { toast.info("Produto já adicionado."); return; }
    setItems([...items, { produto_id: p.id, codigo: p.codigo, descricao: p.descricao, image_path: p.image_path || "", quantidade: 1, preco_unit: priceOf(p), desconto_pct: 0 }]);
    setProdQuery("");
  };

  const updateItem = (idx, field, value) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [field]: value };
    setItems(copy);
  };

  const totals = useMemo(() => {
    let subtotal = 0, desconto = 0;
    items.forEach((it) => {
      const bruto = Number(it.quantidade || 0) * Number(it.preco_unit || 0);
      const d = bruto * (Number(it.desconto_pct || 0) / 100);
      subtotal += bruto; desconto += d;
    });
    return { subtotal, desconto, total: subtotal - desconto };
  }, [items]);

  const lookupCnpj = async () => {
    const digits = cnpj.replace(/\D/g, "");
    if (digits.length !== 14) { toast.error("CNPJ deve ter 14 dígitos."); return; }
    setLooking(true);
    try {
      const { data } = await api.get(`/cnpj/${digits}`);
      setNewCli({
        cnpj: data.cnpj, razao_social: data.razao_social, nome_fantasia: data.nome_fantasia,
        ie: "", suframa: "", endereco: data.endereco, contato: "", email: data.email || "",
        celular: "", telefone: data.telefone || "",
      });
      toast.success("Dados encontrados na Receita.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLooking(false); }
  };

  const saveNewClient = async () => {
    try {
      const { data } = await api.post("/clients", newCli);
      setClients([data, ...clients]);
      setClient(data);
      setCnpjOpen(false); setCnpj(""); setNewCli(null);
      toast.success("Cliente cadastrado.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        cliente_id: client.id, price_table_id: priceTableId, prazo: prazo,
        itens: items.map((it) => ({ ...it, quantidade: Number(it.quantidade), preco_unit: Number(it.preco_unit), desconto_pct: Number(it.desconto_pct) })),
        observacoes: obs, data_faturamento: faturamento,
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Pedido ${data.numero} emitido!`);
      navigate(`/pedidos/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const canNext = step === 0 ? (client && priceTableId && prazo !== "") : step === 1 ? items.length > 0 : true;
  const belowMin = items.length > 0 && totals.total < pedidoMinimo;
  const faltamMin = Math.max(0, pedidoMinimo - totals.total);

  return (
    <div className="max-w-3xl mx-auto" data-testid="order-wizard">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mb-2">Novo Pedido</h1>
      <p className="text-muted-foreground mb-8">Preencha as etapas para emitir o pedido.</p>

      <Stepper step={step} />

      {/* STEP 1 — CLIENTE */}
      {step === 0 && (
        <div className="space-y-6 fade-in">
          <Card className="p-5 shadow-none border-border">
            <div className="flex items-center justify-between mb-4">
              <Label className="text-sm font-semibold text-zinc-900">Selecione o cliente</Label>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setCnpjOpen(true)} data-testid="open-cnpj-dialog">
                <UserPlus size={16} /> Novo por CNPJ
              </Button>
            </div>
            <div className="relative mb-3">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input data-testid="wizard-client-search" value={cliQuery} onChange={(e) => setCliQuery(e.target.value)}
                placeholder="Buscar por razão social, fantasia ou CNPJ..." className="pl-10 h-12" />
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {filteredClients.map((c) => (
                <button key={c.id} onClick={() => setClient(c)} data-testid={`client-option-${c.id}`}
                  className={`w-full text-left p-3 rounded-md border transition-colors ${client?.id === c.id ? "border-accent bg-accent/5" : "border-border hover:bg-zinc-50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-zinc-900 truncate">{c.razao_social}</p>
                      <p className="text-xs text-zinc-500 font-mono">{formatCNPJ(c.cnpj)}</p>
                    </div>
                    <Badge variant="outline" className={c.cadastro_completo ? "border-accent text-accent shrink-0" : "border-amber-500 text-amber-600 shrink-0"}>
                      {c.cadastro_completo ? "Completo" : "Incompleto"}
                    </Badge>
                  </div>
                </button>
              ))}
              {clients.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nenhum cliente. Cadastre por CNPJ.</p>}
            </div>
          </Card>

          <Card className="p-5 shadow-none border-border">
            <Label className="text-sm font-semibold text-zinc-900 mb-4 block">Tabela de preços & prazo</Label>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Tabela de preços</Label>
                <Select value={priceTableId} onValueChange={(v) => { setPriceTableId(v); setItems([]); setPrazo(""); }}>
                  <SelectTrigger className="h-12" data-testid="wizard-price-table"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {priceTables.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Condição de pagamento</Label>
                <Select value={prazo || undefined} onValueChange={setPrazo} disabled={!priceTableId}>
                  <SelectTrigger className="h-12" data-testid="wizard-prazo"><SelectValue placeholder={priceTableId ? "Selecione o prazo" : "Escolha a tabela primeiro"} /></SelectTrigger>
                  <SelectContent>
                    {prazoOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedTable && prazoOptions.length === 0 && (
              <p className="mt-3 text-sm text-amber-600">Esta tabela ainda não tem condições de pagamento cadastradas. Configure em Tabelas de Preço.</p>
            )}
            {selectedTable && prazo !== "" && (
              <div className={`mt-4 flex items-center gap-2 text-sm font-medium ${prazoValido ? "text-accent" : "text-destructive"}`} data-testid="prazo-validation">
                {prazoValido ? <CheckCircle size={18} weight="fill" /> : <XCircle size={18} weight="fill" />}
                {prazoValido ? "PRAZO OK" : "Condição de pagamento inválida para esta tabela"}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* STEP 2 — ITENS */}
      {step === 1 && (
        <div className="space-y-6 fade-in">
          <Card className="p-5 shadow-none border-border">
            <Label className="text-sm font-semibold text-zinc-900 mb-3 block">Adicionar produtos</Label>
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input data-testid="wizard-product-search" value={prodQuery} onChange={(e) => setProdQuery(e.target.value)}
                placeholder="Buscar por código ou descrição..." className="pl-10 h-12" />
            </div>
            {filteredProducts.length > 0 && (
              <div className="mt-2 border border-border rounded-md divide-y divide-border max-h-64 overflow-y-auto">
                {filteredProducts.map((p) => (
                  <button key={p.id} onClick={() => addProduct(p)} data-testid={`product-option-${p.codigo}`}
                    className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-zinc-50">
                    <div className="flex items-center gap-3 min-w-0">
                      {p.image_path ? (
                        <img src={`${API}/products/${p.id}/image?v=${encodeURIComponent(p.image_path)}`} alt="" className="h-10 w-10 rounded object-cover border border-border shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-zinc-100 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-zinc-900 truncate"><span className="font-mono text-zinc-500">{p.codigo}</span> — {p.descricao}</p>
                        <p className="text-xs text-accent font-medium tabular-nums">{brl(priceOf(p))}</p>
                      </div>
                    </div>
                    <Plus size={18} className="text-accent shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </Card>

          {items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">
              <Package size={40} className="mx-auto mb-2 text-zinc-300" />
              Nenhum item adicionado ainda.
            </div>
          ) : (
            <Card className="shadow-none border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-border">
                      {["Produto", "Qtd", "Preço Un.", "Desc.%", "Subtotal", "acoes"].map((h, i) => (
                        <th key={h} className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-zinc-500 ${i >= 1 && i <= 4 ? "text-right" : "text-left"}`}>{h === "acoes" ? "" : h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.map((it, idx) => {
                      const sub = Number(it.quantidade || 0) * Number(it.preco_unit || 0) * (1 - Number(it.desconto_pct || 0) / 100);
                      const overDisc = Number(it.desconto_pct || 0) > effectiveMaxDesc;
                      return (
                        <tr key={it.produto_id} data-testid={`item-row-${it.codigo}`}>
                          <td className="px-3 py-2"><p className="font-mono text-xs text-zinc-500">{it.codigo}</p><p className="text-zinc-800 max-w-[180px] truncate">{it.descricao}</p></td>
                          <td className="px-3 py-2 text-right">
                            <Input type="number" min="1" value={it.quantidade} onChange={(e) => updateItem(idx, "quantidade", e.target.value)}
                              className="w-16 h-9 text-right ml-auto" data-testid={`item-qty-${it.codigo}`} />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-zinc-700" data-testid={`item-price-${it.codigo}`}>
                            {brl(Number(it.preco_unit || 0))}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input type="number" value={it.desconto_pct} onChange={(e) => updateItem(idx, "desconto_pct", e.target.value)}
                              className={`w-16 h-9 text-right ml-auto ${overDisc ? "border-destructive text-destructive" : ""}`} data-testid={`item-disc-${it.codigo}`} />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">{brl(sub)}</td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`item-remove-${it.codigo}`}>
                              <Trash size={18} className="text-zinc-400 hover:text-destructive" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center p-4 border-t border-border bg-zinc-50">
                <div className="text-xs">
                  <p className="text-muted-foreground">Desconto máximo permitido: {effectiveMaxDesc}%</p>
                  <p className="text-muted-foreground">Pedido mínimo: {brl(pedidoMinimo)}</p>
                </div>
                <div className="text-right">
                  <span className="text-sm text-zinc-600 mr-3">Total</span>
                  <span className={`font-heading text-2xl font-bold tabular-nums ${belowMin ? "text-destructive" : "text-accent"}`}>{brl(totals.total)}</span>
                </div>
              </div>
              {belowMin && (
                <div className="px-4 py-2.5 bg-destructive/10 border-t border-destructive/20 text-sm text-destructive font-medium" data-testid="min-order-warning">
                  Pedido mínimo é {brl(pedidoMinimo)}. Faltam {brl(faltamMin)} para atingir o mínimo.
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* STEP 3 — REVISÃO */}
      {step === 2 && (
        <div className="space-y-6 fade-in">
          <Card className="p-5 shadow-none border-border">
            <div className="flex items-center gap-2 mb-3"><Buildings size={20} className="text-accent" /><h3 className="font-heading font-semibold">Cliente</h3></div>
            <p className="font-medium text-zinc-900">{client?.razao_social}</p>
            <p className="text-sm text-zinc-500 font-mono">{formatCNPJ(client?.cnpj || "")}</p>
            <div className="flex gap-2 mt-3">
              <Badge variant="secondary">{selectedTable?.name}</Badge>
              <Badge variant="outline" className={prazoValido ? "border-accent text-accent" : "border-destructive text-destructive"}>
                {prazo} — {prazoValido ? "PRAZO OK" : "INVÁLIDO"}
              </Badge>
            </div>
          </Card>

          <Card className="shadow-none border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-zinc-50 border-b border-border">
                {["Produto", "Qtd", "Subtotal"].map((h, i) => <th key={h} className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-zinc-500 ${i ? "text-right" : "text-left"}`}>{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-border">
                {items.map((it) => {
                  const sub = Number(it.quantidade) * Number(it.preco_unit) * (1 - Number(it.desconto_pct) / 100);
                  return <tr key={it.produto_id}><td className="px-4 py-2 text-zinc-800">{it.descricao}</td><td className="px-4 py-2 text-right tabular-nums">{it.quantidade}</td><td className="px-4 py-2 text-right tabular-nums font-medium">{brl(sub)}</td></tr>;
                })}
              </tbody>
            </table>
            <div className="flex justify-end p-4 border-t border-border">
              <div className="w-60 space-y-1 text-sm">
                <div className="flex justify-between text-zinc-600"><span>Subtotal</span><span className="tabular-nums">{brl(totals.subtotal)}</span></div>
                <div className="flex justify-between text-zinc-600"><span>Desconto</span><span className="tabular-nums">- {brl(totals.desconto)}</span></div>
                <div className="flex justify-between pt-2 border-t border-border"><span className="font-semibold">Total</span><span className={`font-heading text-xl font-bold tabular-nums ${belowMin ? "text-destructive" : "text-accent"}`}>{brl(totals.total)}</span></div>
              </div>
            </div>
            {belowMin && (
              <div className="px-4 py-2.5 bg-destructive/10 border-t border-destructive/20 text-sm text-destructive font-medium" data-testid="min-order-warning-review">
                Pedido mínimo é {brl(pedidoMinimo)}. Faltam {brl(faltamMin)} para poder emitir.
              </div>
            )}
          </Card>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Data de faturamento</Label>
              <Input type="date" value={faturamento} onChange={(e) => setFaturamento(e.target.value)} className="h-12" data-testid="wizard-faturamento" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Observações</Label>
            <Textarea value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observações do pedido..." rows={3} data-testid="wizard-obs" />
          </div>
        </div>
      )}

      {/* footer actions */}
      <div className="flex justify-between mt-8 sticky bottom-0 bg-background py-4">
        <Button variant="outline" onClick={() => step === 0 ? navigate("/pedidos") : setStep(step - 1)} className="gap-2" data-testid="wizard-back">
          <ArrowLeft size={18} /> {step === 0 ? "Cancelar" : "Voltar"}
        </Button>
        {step < 2 ? (
          <Button disabled={!canNext} onClick={() => setStep(step + 1)} className="bg-accent hover:bg-accent/90 text-white gap-2" data-testid="wizard-next">
            Continuar <ArrowRight size={18} />
          </Button>
        ) : (
          <Button disabled={submitting || belowMin} onClick={submit} className="bg-accent hover:bg-accent/90 text-white gap-2" data-testid="wizard-submit">
            <CheckCircle size={18} weight="bold" /> {submitting ? "Emitindo..." : "Emitir Pedido"}
          </Button>
        )}
      </div>

      {/* CNPJ dialog */}
      <Dialog open={cnpjOpen} onOpenChange={setCnpjOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Novo cliente por CNPJ</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input value={cnpj} onChange={(e) => setCnpj(formatCNPJ(e.target.value))} placeholder="00.000.000/0000-00" className="h-11" data-testid="cnpj-input" />
            <Button onClick={lookupCnpj} disabled={looking} className="bg-zinc-950 text-white gap-2 shrink-0" data-testid="cnpj-lookup-btn">
              <CloudArrowDown size={18} /> {looking ? "..." : "Buscar"}
            </Button>
          </div>
          {newCli && (
            <div className="space-y-3 mt-2">
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Razão Social</Label><Input value={newCli.razao_social} onChange={(e) => setNewCli({ ...newCli, razao_social: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Inscrição Estadual</Label><Input value={newCli.ie} onChange={(e) => setNewCli({ ...newCli, ie: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Contato</Label><Input value={newCli.contato} onChange={(e) => setNewCli({ ...newCli, contato: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">E-mail</Label><Input value={newCli.email} onChange={(e) => setNewCli({ ...newCli, email: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Celular</Label><Input value={newCli.celular} onChange={(e) => setNewCli({ ...newCli, celular: e.target.value })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCnpjOpen(false)}>Cancelar</Button>
            <Button disabled={!newCli} onClick={saveNewClient} className="bg-accent hover:bg-accent/90 text-white" data-testid="save-new-client">Salvar cliente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
